import { supabase, allowOfflineFallback } from './mockData';
import { Owner } from '../types';

export async function getOwners(): Promise<Owner[]> {
  try {
    const { data, error } = await supabase.from('owners').select('*').order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    
    // Supabase'den gelen veriyi Owner interface'ine uygun hale getir
    return (data || []).map((item: any) => ({
      id: item.id,
      name: item.name || '',
      phone: item.phone || '',
      email: item.email || '',
      address: item.address || '',
      chipNumber: item.chip_number || '',
      passportNumber: item.passport_number || '',
      createdAt: item.created_at || new Date().toISOString()
    })) as Owner[];
  } catch (error) {
    console.error('getOwners:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const { mockOwners } = await import('./mockData');
    return mockOwners;
  }
}

export async function addOwner(owner: Omit<Owner, 'id' | 'createdAt'>): Promise<Owner> {
  try {
    // Mevcut kullanıcının clinic_id'sini al
    let clinicId: string | null = null;
    try {
      const stored = localStorage.getItem('vetklinik_user');
      if (stored) {
        const u = JSON.parse(stored);
        clinicId = u?.clinicId || null;
      }
    } catch {}

    const ownerData: any = {
      name: owner.name,
      phone: owner.phone,
      email: (owner.email || '').trim().toLowerCase(),
      address: owner.address,
      chip_number: owner.chipNumber,
      passport_number: owner.passportNumber,
    };
    if (clinicId) ownerData.clinic_id = clinicId;

    const { data, error } = await supabase.from('owners').insert([ownerData]).select().single();
    if (error) throw new Error(error.message);
    
    return {
      ...owner,
      id: data.id,
      createdAt: data.created_at || new Date().toISOString()
    } as Owner;
  } catch (error) {
    console.error('owners insert:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const newOwner: Owner = {
      ...owner,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    
    const existingOwners = JSON.parse(localStorage.getItem('owners') || '[]');
    existingOwners.push(newOwner);
    localStorage.setItem('owners', JSON.stringify(existingOwners));
    
    return newOwner;
  }
}

export async function updateOwner(id: string, owner: Partial<Owner>): Promise<Owner> {
  try {
    const updateData: any = {};
    
    if (owner.name !== undefined) updateData.name = owner.name;
    if (owner.phone !== undefined) updateData.phone = owner.phone;
    if (owner.email !== undefined) updateData.email = (owner.email || '').trim().toLowerCase();
    if (owner.address !== undefined) updateData.address = owner.address;
    if (owner.chipNumber !== undefined) updateData.chip_number = owner.chipNumber;
    if (owner.passportNumber !== undefined) updateData.passport_number = owner.passportNumber;

    const { data, error } = await supabase.from('owners').update(updateData).eq('id', id).select().single();
    if (error) throw new Error(error.message);
    
    return {
      ...owner,
      id: data.id,
      createdAt: data.created_at || new Date().toISOString()
    } as Owner;
  } catch (error) {
    console.error('owners update:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingOwners = JSON.parse(localStorage.getItem('owners') || '[]');
    const ownerIndex = existingOwners.findIndex((o: Owner) => o.id === id);
    
    if (ownerIndex !== -1) {
      existingOwners[ownerIndex] = { ...existingOwners[ownerIndex], ...owner };
      localStorage.setItem('owners', JSON.stringify(existingOwners));
      return existingOwners[ownerIndex];
    }
    
    throw new Error('Owner bulunamadı');
  }
}

export async function deleteOwner(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('owners').delete().eq('id', id);
    if (error) throw new Error(error.message);
  } catch (error) {
    console.error('owners delete:', error);
    if (!allowOfflineFallback()) {
      throw error instanceof Error ? error : new Error(String(error));
    }
    const existingOwners = JSON.parse(localStorage.getItem('owners') || '[]');
    const filteredOwners = existingOwners.filter((o: Owner) => o.id !== id);
    localStorage.setItem('owners', JSON.stringify(filteredOwners));
  }
}